import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './OrgSelect.scss';

export interface OrgSelectOption {
  value: string;
  label: string;
  // Shown in the closed trigger instead of `label` when set (e.g. a compact "+1"
  // vs. the full "United States (+1)" used in the open list) — falls back to
  // `label` when omitted, so existing callers are unaffected.
  triggerLabel?: string;
  disabled?: boolean;
}

interface OrgSelectProps {
  value: string;
  options: OrgSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  // Visual style for the trigger — passed in so it matches the sibling inputs exactly.
  triggerStyle?: React.CSSProperties;
}

// Accessible collapsible-listbox dropdown (ARIA combobox pattern) that replaces the
// native <select>. The option list is a normal absolutely-positioned element in the
// page DOM, so — unlike a native <select> popup — it anchors correctly under the
// trigger in every environment, including Chrome DevTools device emulation where a
// native popup renders in screen coordinates and lands far from the control.
export default function OrgSelect({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  triggerStyle,
}: OrgSelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef<{ buffer: string; timer: number | null }>({ buffer: '', timer: null });
  const baseId = useId();

  const selectedIndex = options.findIndex(o => o.value === value);
  const selectedLabel = selectedIndex >= 0 ? (options[selectedIndex].triggerLabel ?? options[selectedIndex].label) : '';
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  const firstEnabled = useCallback(() => options.findIndex(o => !o.disabled), [options]);
  const lastEnabled = useCallback(() => {
    for (let i = options.length - 1; i >= 0; i--) if (!options[i].disabled) return i;
    return -1;
  }, [options]);
  // Next/previous enabled index, walking in `dir` and stopping at the ends (no wrap).
  const step = useCallback((from: number, dir: 1 | -1) => {
    let i = from;
    while (true) {
      const next = i + dir;
      if (next < 0 || next >= options.length) return i; // clamp at the edges
      i = next;
      if (!options[i].disabled) return i;
    }
  }, [options]);

  const openList = useCallback(() => {
    if (disabled) return;
    // Start the highlight on the current selection, or the first selectable option.
    const start = selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : firstEnabled();
    setActiveIndex(start);
    setOpen(true);
  }, [disabled, selectedIndex, options, firstEnabled]);

  const closeList = useCallback((focusButton = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const commit = useCallback((index: number) => {
    const opt = options[index];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closeList();
  }, [options, onChange, closeList]);

  // Close when focus/click leaves the component.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeList(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, closeList]);

  // Keep the active option scrolled into view while navigating.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const onTypeahead = useCallback((char: string) => {
    const t = typeahead.current;
    t.buffer += char.toLowerCase();
    if (t.timer) window.clearTimeout(t.timer);
    t.timer = window.setTimeout(() => { t.buffer = ''; t.timer = null; }, 500);
    const match = options.findIndex(o => !o.disabled && o.label.toLowerCase().startsWith(t.buffer));
    if (match >= 0) setActiveIndex(match);
  }, [options]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => (i < 0 ? firstEnabled() : step(i, 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => (i < 0 ? lastEnabled() : step(i, -1)));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled());
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(lastEnabled());
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        closeList();
        break;
      case 'Tab':
        closeList(false); // let focus move on naturally
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) onTypeahead(e.key);
    }
  };

  return (
    <div className="org-select" ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className="org-select__trigger"
        style={triggerStyle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        onClick={() => (open ? closeList() : openList())}
        onKeyDown={onKeyDown}
      >
        <span className="org-select__value">{selectedLabel}</span>
        <span className={`org-select__chevron${open ? ' is-open' : ''}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <ul className="org-select__list" role="listbox" ref={listRef} tabIndex={-1} aria-label={ariaLabel}>
          {options.map((o, i) => {
            const isSelected = o.value === value;
            const isActive = i === activeIndex;
            const cls = [
              'org-select__option',
              isActive ? 'is-active' : '',
              isSelected ? 'is-selected' : '',
              o.disabled ? 'is-disabled' : '',
            ].filter(Boolean).join(' ');
            return (
              <li
                key={o.value}
                id={optionId(i)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={o.disabled || undefined}
                className={cls}
                // mousedown (not click) so the trigger keeps focus; preventDefault
                // stops the button from blurring before we handle the selection.
                onMouseDown={e => { e.preventDefault(); if (!o.disabled) commit(i); }}
                onMouseEnter={() => { if (!o.disabled) setActiveIndex(i); }}
              >
                <span className="org-select__check" aria-hidden="true">{isSelected ? '✓' : ''}</span>
                <span className="org-select__label">{o.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
